// features/sa-lab/SaLabPage.jsx
//
// SA Lab: banco di prova interno del Self-Assessment conversazionale servito
// da vLLM in streaming. Serve a provare i profili e a misurare la velocità
// (estrazione, primo testo, totale). Ambiente isolato: nessun dato finisce
// sul profilo della risorsa. Rotta protetta /sa-lab (utenti HR/admin).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useKmClient } from '../../lib/useKmClient.js';
import { useKmQuery } from '../knowledge-management/hooks/useKmQuery.js';
import ErrorDetails from '../knowledge-management/components/ErrorDetails.jsx';
import MessageBubble from '../self-assessment/components/MessageBubble.jsx';
import QuickReplies from '../self-assessment/components/QuickReplies.jsx';
import ChatComposer from '../self-assessment/components/ChatComposer.jsx';
import TypingIndicator from '../self-assessment/components/TypingIndicator.jsx';
import AvvioPanel from './components/AvvioPanel.jsx';
import AvanzamentoPanel from './components/AvanzamentoPanel.jsx';
import LatenzaBadge, { classeLatenza, fmtMs } from './components/LatenzaBadge.jsx';
import { useSaLabSession } from './hooks/useSaLabSession.js';
// global.css (token e classi card/btn/field-*) è già importato da index.js
import '../../styles/self-assessment.css';
import './sa-lab.css';

// Media e massimo di una serie, ignorando i valori mancanti
function statistiche(valori) {
  const v = valori.filter((x) => typeof x === 'number' && Number.isFinite(x));
  if (!v.length) return { media: null, max: null, n: 0 };
  return { media: v.reduce((a, b) => a + b, 0) / v.length, max: Math.max(...v), n: v.length };
}

function RiepilogoLatenze({ messaggi }) {
  const tempi = messaggi.filter((m) => m.ruolo === 'assistant' && m.tempi).map((m) => m.tempi);
  const primo = statistiche(tempi.map((t) => t.primo_token_ms));
  const totale = statistiche(tempi.map((t) => t.totale_ms));
  const estrazione = statistiche(tempi.map((t) => t.estrazione_ms));

  return (
    <div className="card salab-riepilogo">
      <h2 className="section-title salab-h2">Latenze della sessione</h2>
      {tempi.length === 0 ? (
        <p className="salab-muted">Nessun turno misurato.</p>
      ) : (
        <table className="salab-tabella">
          <thead>
            <tr><th /><th>media</th><th>max</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Primo testo</td>
              <td><span className={`salab-lat ${classeLatenza(primo.media)}`}>{fmtMs(primo.media)}</span></td>
              <td><span className={`salab-lat ${classeLatenza(primo.max)}`}>{fmtMs(primo.max)}</span></td>
            </tr>
            <tr>
              <td>Totale</td>
              <td>{fmtMs(totale.media)}</td>
              <td>{fmtMs(totale.max)}</td>
            </tr>
            {estrazione.n > 0 && (
              <tr>
                <td>Estrazione</td>
                <td>{fmtMs(estrazione.media)}</td>
                <td>{fmtMs(estrazione.max)}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      <p className="salab-muted salab-xs">
        {tempi.length} turni misurati · soglie sul primo testo: verde &lt; 1000 ms, arancio &lt; 2500 ms, rosso oltre.
      </p>
    </div>
  );
}

export default function SaLabPage() {
  const kmClient = useKmClient('km');
  const opzioni = useKmQuery(() => kmClient.get('/lab/opzioni'), []);
  const s = useSaLabSession(kmClient);
  const [bozza, setBozza] = useState('');
  const scrollRef = useRef(null);

  // Scorre in fondo a ogni nuovo pezzo di testo
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [s.messaggi, s.attesaPrimoTesto, s.quickReplies]);

  const modelloCorrente = useMemo(() => {
    const lista = Array.isArray(opzioni.data?.modelli) ? opzioni.data.modelli : [];
    return lista.find((m) => m.id === s.sessione?.modello);
  }, [opzioni.data, s.sessione]);

  const invia = () => {
    if (!bozza.trim()) return;
    s.inviaTesto(bozza);
    setBozza('');
  };

  // Le quick reply del contratto hanno "etichetta"; QuickReplies usa "label"
  const opzioniQr = s.quickReplies.map((q) => ({ ...q, label: q.etichetta ?? String(q.id) }));

  return (
    <div className="km-scope salab-page">
      <header className="salab-header">
        <div>
          <h1 className="salab-titolo">SA Lab · Self-Assessment conversazionale (prova)</h1>
          <p className="salab-sottotitolo">
            Ambiente di prova isolato: nessun dato viene salvato sul profilo della risorsa. Usa solo dati di prova.
          </p>
        </div>
        <Link to="/" className="btn btn-secondary">Torna all’Hub</Link>
      </header>

      <div className="salab-layout">
        <main className="salab-main">
          {!s.sessione ? (
            <AvvioPanel
              opzioni={opzioni.data}
              statoOpzioni={opzioni.status}
              erroreOpzioni={opzioni.error}
              onRiprova={opzioni.refetch}
              onAvvia={s.avvia}
              disabilitato={s.avvioInCorso}
            />
          ) : (
            <div className="card salab-sessione-bar">
              <span className="badge badge-neutral">{s.sessione.profilo}</span>
              <span className="badge badge-neutral" title={s.sessione.modello}>
                {modelloCorrente?.etichetta || s.sessione.modello}
                {modelloCorrente ? ` · ${modelloCorrente.locale ? 'locale' : 'cloud'}` : ''}
              </span>
              {s.fase && <span className="salab-muted salab-xs">fase: {s.fase}</span>}
              {s.chiusa && <span className="badge badge-success">Sessione conclusa</span>}
              <span className="salab-spazio" />
              <button type="button" className="btn-link salab-xs" onClick={s.ricarica} disabled={s.inStreaming}>
                Riallinea dal server
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => { s.reset(); setBozza(''); }}>
                Nuova sessione
              </button>
            </div>
          )}

          {s.errore && (
            <div className="salab-errore" role="alert">
              <div className="salab-errore-testo">
                <strong>Errore:</strong> {s.errore.messaggio}
                <ErrorDetails error={s.errore.dettaglio} />
              </div>
              <button type="button" className="btn-link" onClick={s.chiudiErrore} aria-label="Chiudi errore">✕</button>
            </div>
          )}

          {(s.sessione || s.avvioInCorso) && (
            <div className="card salab-chat">
              <div className="salab-chat-scroll" ref={scrollRef}>
                <div className="salab-chat-colonna">
                  {s.messaggi.map((m) => (
                    <div key={m.id} className="salab-turno">
                      <MessageBubble role={m.ruolo === 'user' ? 'user' : 'bot'} text={m.testo} isError={m.isError} />
                      {m.ruolo === 'assistant' && <LatenzaBadge tempi={m.tempi} meta={m.meta} />}
                    </div>
                  ))}
                  {(s.avvioInCorso || s.attesaPrimoTesto) && <TypingIndicator />}
                  {!s.inStreaming && !s.chiusa && (
                    <QuickReplies options={opzioniQr} onSelect={s.inviaScelta} disabled={s.inStreaming} />
                  )}
                  {s.chiusa && (
                    <p className="salab-muted salab-xs salab-centro">
                      Conversazione conclusa. Usa «Nuova sessione» per ricominciare.
                    </p>
                  )}
                </div>
              </div>
              <ChatComposer
                value={bozza}
                onChange={setBozza}
                onSend={invia}
                disabled={!s.sessione || s.inStreaming || s.avvioInCorso || s.chiusa}
                placeholder={s.chiusa ? 'Sessione conclusa' : 'Scrivi come risponderebbe la persona…'}
              />
            </div>
          )}
        </main>

        <aside className="salab-side">
          <AvanzamentoPanel stato={s.stato} />
          <RiepilogoLatenze messaggi={s.messaggi} />
        </aside>
      </div>
    </div>
  );
}
